import React, { useState, useEffect, useCallback, useMemo } from 'react'
import Header from 'src/layouts/Header'
import Footer from 'src/layouts/Footer'
import InputCounter from 'src/components/InputCounter'
import ColorBox from 'src/components/ColorBox'
import ImageUpload from 'src/components/ImageUpload'
import MultipleImageUpload from 'src/components/MultipleImageUpload'
import axios from 'src/api/axios'
import { NotificationManager } from 'react-notifications'
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { showModal } from 'src/redux/actions/modalActions'
import NotFound from 'src/pages/NotFound'
import useSEO from 'src/hooks/useSEO'

const EditAd = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { id } = useParams()
  const [article, setArticle] = useState({})
  const [articleNotFound, setArticleNotFound] = useState(false)
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({})

  const [adOptions, setAdOptions] = useState({})
  const [isImageUploading, setIsImageUploading] = useState(false)
  const [isGalleryUploading, setIsGalleryUploading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset loading states when route changes
  useEffect(() => {
    setIsSubmitting(false)
    setIsImageUploading(false)
    setIsGalleryUploading(false)
  }, [location.key])

  const imageUploadRef = React.useRef(null)
  const galleryUploadRef = React.useRef(null)

  const fetchAdOptions = useCallback(async () => {
    try {
      const response = await axios.get(`/api/resources/post-ad?ad_type=${formData.ad_type}`)
      setAdOptions(response.data.data)
    } catch (error) {
      NotificationManager.error('Error fetching ad options')
    }
  }, [formData.ad_type])

  const fetchArticle = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(`/api/my/article/${id}`)
      const articleData = response.data.data
      
      // Check if user has permission to edit the article
      // If not, show 404 (security protection - as world websites do)
      if (!articleData) {
          setArticleNotFound(true)
          setLoading(false)
          return
      }

      setArticle(articleData)
      
      // Map attributes to simple key-value pairs for the form
      const attributes = {}
      if (articleData.attributes) {
        Object.entries(articleData.attributes).forEach(([key, value]) => {
          attributes[key] = value
        })
      }

      setFormData({
        title: articleData.title || '',
        description: articleData.description || '',
        price: articleData.price || '',
        currency: articleData.currency || 'EUR',
        price_negotiable: articleData.price_negotiable || false,
        ad_type: articleData.ad_type || 'for-sale',
        featured_image: articleData.featured_image_id || '',
        featured_image_url: articleData.featured_image_url || '',
        gallery: articleData.gallery || [],
        ...attributes
      })
      setLoading(false)
    } catch (error) {
      if (error.response?.status === 404) {
        setArticleNotFound(true)
      } else {
        NotificationManager.error('Error fetching ad data')
      }
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchArticle()
  }, [fetchArticle])

  useEffect(() => {
    if (formData.ad_type) {
      fetchAdOptions()
    }
  }, [formData.ad_type, fetchAdOptions])

  const handleChange = useCallback(event => {
    const { name, value, type, checked } = event.target
    setFormData(prevFormData => {
      if (name.endsWith('[]')) {
        const arrayName = name.slice(0, -2)
        const currentArray = prevFormData[arrayName] || []

        if (event.target.checked) {
          setErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors[arrayName]
            return newErrors
          })
          return { ...prevFormData, [arrayName]: [...currentArray, value] }
        } else {
          setErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors[arrayName]
            return newErrors
          })
          return { ...prevFormData, [arrayName]: currentArray.filter(item => item !== value) }
        }
      }
      
      // Handle single checkbox (not array)
      if (type === 'checkbox' && !name.endsWith('[]')) {
        setErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors[name]
          return newErrors
        })
        return { ...prevFormData, [name]: checked }
      }
      
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
      return { ...prevFormData, [name]: value }
    })
  }, [])

  const handleImageChange = useCallback((data) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      featured_image_url: data?.public_url ?? '',
      featured_image: data?.path ?? ''
    }))
    if (data) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.images
        return newErrors
      })
    }
  }, [])

  const handleGalleryChange = useCallback((data) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      gallery: data.map(item => ({
        id: item.id,
        name: item.name,
        public_url: item.url,
        original_url: item.url,
        preview_url: item.url,
        isNew: item.isNew,
        path: item.isNew ? item.path : undefined
      }))
    }))
    if (data && data.length > 0) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.images
        return newErrors
      })
    }
  }, [])

  const performSubmit = useCallback(async () => {
    setIsSubmitting(true)
    
    // Cancel any ongoing uploads
    if (imageUploadRef.current) imageUploadRef.current.cancelUpload();
    if (galleryUploadRef.current) galleryUploadRef.current.cancelAllUploads();

    const dataToSend = { ...formData }
    // Clean up gallery for backend (only send necessary info)
    if (dataToSend.gallery) {
      dataToSend.gallery = dataToSend.gallery.map(item => ({
        id: item.id,
        path: item.path,
        isNew: item.isNew
      }))
    }

    try {
      const response = await axios.patch(`/api/my/article/${id}`, dataToSend, {
        disableNotification: true // Disable interceptor notifications, we handle errors manually
      })
      if (response.status >= 200 && response.status < 300) {
        NotificationManager.success('Ad successfully updated')
        navigate(`/ad/${id}/${response.data.data.slug}`)
      }
    } catch (error) {
      if (error.response?.status === 422) {
        const errors = error.response.data.errors || {}
        setErrors(errors)

        // Fokusiraj na prvo polje sa greškom
        const firstErrorField = Object.keys(errors)[0]
        if (firstErrorField) {
          const fieldMap = {
            'gender': 'litter-male-count',
            'price': 'price',
            'images': 'media-upload-section'
          }
          const fieldName = fieldMap[firstErrorField] || firstErrorField
          const element = document.querySelector(`[name="${fieldName}"]`) || document.querySelector(`[name="${fieldName}[]"]`) || document.getElementById(fieldName)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
            if (element.focus) element.focus({ preventScroll: true })
          }
        }

        NotificationManager.error(error.response.data.message || 'Validation error')
      } else {
        NotificationManager.error(error.response?.data?.message || 'Error updating ad')
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [id, formData, navigate])

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault()
    
    const isAnyImageUploading = isImageUploading || isGalleryUploading
    if (isAnyImageUploading) {
      dispatch(showModal('CONFIRMATION_MODAL', {
        title: 'Upload in Progress',
        message: 'Some images are still being uploaded. Would you like to wait for all uploads to complete, or submit the ad now (incomplete uploads will not be saved)?',
        confirmText: 'Submit anyway',
        cancelText: 'Wait',
        iconType: 'warning',
        onConfirm: performSubmit
      }))
      return
    }

    performSubmit()
  }, [isImageUploading, isGalleryUploading, performSubmit, dispatch])

  useSEO({
    title: `Edit Ad: ${article.title || ''} - SphynxPlace.com`,
    description: `Edit your Sphynx cat listing on SphynxPlace.com.`,
    robots: 'noindex, nofollow',
  });

  if (loading) {
    return (
      <>
        <Header />
        <div className='bg-[#F7F7F7] py-[35px] min-h-screen flex items-center justify-center'>
          <div className="text-center">
            <div role="status">
                <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-purple-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                </svg>
                <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  if (articleNotFound) {
    return <NotFound />
  }

  return (
    <>
      <Header />
      <div className='bg-[#F7F7F7] py-[20px] md:py-[35px] min-h-screen flex justify-center'>
        <div className="max-w-[953px] w-full py-[15px] md:py-[25px] bg-white border border-[#EBEBEB] rounded-lg mb-5" style={{ boxShadow: '0px 3px 6px #8D8D8D36' }}>
          <p className='my-auto uppercase font-bold text-[20px] md:text-[24px] leading-7 px-2 md:px-6 lg:px-10 pb-[15px] md:pb-[25px]'>Edit Ad</p>
          <hr />
          <form onSubmit={handleSubmit}>
            <div className='px-4 md:px-6 lg:px-10 pt-5 pb-2.5'>
              <div className='flex flex-col md:flex-row justify-between gap-6 md:gap-1 mb-11'>
                <div className='w-full md:max-w-[420px]'>
                  <div>
                    <label htmlFor="title" className="block mb-2 text-base font-medium text-[#0D0D0D] dark:text-white">Title</label>
                    <input name="title" value={formData.title || ''} onChange={handleChange} type="text" id="title" className={`max-h-[36px] bg-white border ${errors.title ? 'border-red-500' : 'border-gray-300'} text-[#0D0D0D] text-sm rounded focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`} />
                    {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title[0]}</p>}
                  </div>
                  {formData.ad_type !== 'for-adoption' && (
                    <>
                      <div htmlFor="price" className="block mt-5 mb-2 text-base font-medium text-[#0D0D0D] dark:text-white">Price</div>
                      <div className="flex items-center">
                        <div className="relative max-w-[180px]">
                          <input name="price" value={formData.price || ''} onChange={handleChange} type="text" id="price" disabled={formData.price_negotiable} className="max-h-[36px] pr-[60px] bg-white text-left border border-gray-300 text-[#0D0D0D] text-sm rounded focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" />
                          <div className='absolute right-0 top-0'>
                            <select name="currency" value={formData.currency || ''} onChange={handleChange} id="currency" disabled={formData.price_negotiable} className="font-bold max-h-[36px] bg-white border border-gray-300 text-gray-900 text-base border-l-0 rounded-r border-s-gray-100 dark:border-s-gray-700 border-s-2 focus:ring-blue-500 focus:border-blue-500 block w-full p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                              {adOptions.currencies && Object.entries(adOptions.currencies).map(([key, value]) => (
                                <option key={key} value={key}>{value}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center ml-4">
                          <input name="price_negotiable" checked={formData.price_negotiable || false} onChange={handleChange} id="price_negotiable" type="checkbox" className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                          <label htmlFor="price_negotiable" className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">Negotiable</label>
                        </div>
                      </div>
                      {errors.price && (
                        <p className="mt-1 text-sm text-red-500">{Array.isArray(errors.price) ? errors.price[0] : errors.price}</p>
                      )}
                    </>
                  )}
                  {adOptions['color'] && (
                    <div className="mt-7 mb-2 text-base font-medium text-[#0D0D0D] dark:text-white">
                      Color
                      <div className="grid grid-cols-2 gap-2 -mt-2 max-w-[250px] pt-2">
                        {adOptions.color.options && Object.entries(adOptions.color.options).map(([key, value]) => (
                          <ColorBox key={key} name="color[]" type="checkbox" value={key} spanText={value} onChange={handleChange} checked={formData.color?.includes(key)} />
                        ))}
                      </div>
                      {errors.color && (
                        <p className="mt-1 text-sm text-red-500">{Array.isArray(errors.color) ? errors.color[0] : errors.color}</p>
                      )}
                    </div>
                  )}
                  {adOptions['date-of-birth'] && (
                    <>
                      <label htmlFor="date-of-birth" className="block mt-5 mb-2 text-base font-medium text-[#0D0D0D] dark:text-white">Date of Birth</label>
                      <input name="date-of-birth" value={formData['date-of-birth'] || ''} onChange={handleChange} type="date" id="date-of-birth" className="max-h-[36px] max-w-[180px] bg-white border border-gray-300 text-[#0D0D0D] text-sm rounded focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" />
                    </>
                  )}
                </div>
                <div className='w-full md:max-w-[380px]'>
                  {adOptions['litter-male-count'] && (
                    <>
                      <label htmlFor="litter-male-count" className="block text-base font-medium text-[#0D0D0D] dark:text-white">
                        {formData.ad_type === 'for-adoption' ? 'Cats for adoption' : formData.ad_type === 'for-stud' ? 'Stud males available' : 'Pets in litter'}
                      </label>
                      <div className="space-y-4 mt-2">
                        <div className="flex items-center">
                          <span className="text-base font-medium text-[#777777] w-[70px]">Male</span>
                          <div className="flex items-center">
                            <InputCounter 
                              name="litter-male-count"
                              value={formData['litter-male-count']}
                              onChange={handleChange}
                              step={1}
                            />
                          </div>
                        </div>
                        {adOptions['litter-female-count'] && formData.ad_type !== 'for-stud' && (
                          <div className="flex items-center">
                            <span className="text-base font-medium text-[#777777] w-[70px]">Female</span>
                            <div className="flex items-center">
                              <InputCounter
                                name="litter-female-count" 
                                value={formData['litter-female-count']}
                                onChange={handleChange}
                                step={1}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {adOptions['energy-level'] && (
                    <>
                      <label htmlFor="energy-level" className="block text-base font-medium text-[#0D0D0D] dark:text-white mt-5 mb-2">Energy level</label>
                      <select id="energy-level" name="energy-level" value={formData['energy-level'] || ''} onChange={handleChange} className="bg-white border max-h-[36px] border-gray-300 text-[#777777] text-base rounded focus:ring-blue-500 focus:border-blue-500 block w-full h-full p-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                        <option value="">Select energy level</option>
                        {adOptions['energy-level']?.options && Object.entries(adOptions['energy-level'].options).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                        ))}
                      </select>
                    </>
                  )}
                  {adOptions['home-lifestyle'] && (
                    <>
                      <label htmlFor="home-lifestyle" className="block text-base font-medium text-[#0D0D0D] dark:text-white mt-3 mb-2">Home lifestyle</label>
                      <select id="home-lifestyle" name="home-lifestyle" value={formData['home-lifestyle'] || ''} onChange={handleChange} className="bg-white border max-h-[36px] border-gray-300 text-[#777777] text-base rounded focus:ring-blue-500 focus:border-blue-500 block w-full h-full p-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                        <option value="">Select home lifestyle</option>
                        {adOptions['home-lifestyle']?.options && Object.entries(adOptions['home-lifestyle'].options).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                        ))}
                      </select>
                    </>
                  )}
                  {adOptions['litter-habits'] && (
                    <>
                      <label htmlFor="litter-habits" className="block text-base font-medium text-[#0D0D0D] dark:text-white mt-3 mb-2">Litter habits</label>
                      <select id="litter-habits" name="litter-habits" value={formData['litter-habits'] || ''} onChange={handleChange} className="bg-white border max-h-[36px] border-gray-300 text-[#777777] text-base rounded focus:ring-blue-500 focus:border-blue-500 block w-full h-full p-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                        <option value="">Select litter habits</option>
                        {adOptions['litter-habits']?.options && Object.entries(adOptions['litter-habits'].options).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                        ))}
                      </select>
                    </>
                  )}
                  {adOptions['grooming-level'] && (
                    <>
                      <label htmlFor="grooming-level" className="block text-base font-medium text-[#0D0D0D] dark:text-white mt-3 mb-2">Grooming level</label>
                      <select id="grooming-level" name="grooming-level" value={formData['grooming-level'] || ''} onChange={handleChange} className="bg-white border max-h-[36px] border-gray-300 text-[#777777] text-base rounded focus:ring-blue-500 focus:border-blue-500 block w-full h-full p-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                        <option value="">Select grooming level</option>
                        {adOptions['grooming-level']?.options && Object.entries(adOptions['grooming-level'].options).map(([key, value]) => (
                          <option key={key} value={key}>{value}</option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              </div>

              {adOptions['health-and-documentation'] && (
                <>
                  <hr />
                  <p className="font-bold text-[15px] md:text-[17px] leading-6 pb-[25px] md:pb-[33px] pt-[25px] md:pt-[36px]">Cat health & Documentation</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 mb-10">
                    {adOptions['health-and-documentation']?.options && Object.entries(adOptions['health-and-documentation'].options).map(([key, value], index) => (
                      <li key={index}>
                        <div className="flex items-center mr-4">
                          <input name="health-and-documentation[]" onChange={handleChange} id={`health-checkbox-${index}`} type="checkbox" value={key} checked={formData['health-and-documentation']?.includes(key)} className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                          <label htmlFor={`health-checkbox-${index}`} className="ml-2 text-base font-medium text-gray-900 dark:text-gray-300">{value}</label>
                        </div>
                      </li>
                    ))}
                    <li>
                      <div className="flex items-center mr-4">
                        <input name="pedigree[]" onChange={handleChange} id="pedigree-checkbox" type="checkbox" value="pedigree" checked={formData['pedigree']?.includes('pedigree')} className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                        <label htmlFor="pedigree-checkbox" className="ml-2 text-base font-medium text-gray-900 dark:text-gray-300">Pedigree available</label>
                      </div>
                    </li>
                  </ul>
                </>
              )}

              <hr />
              <p id="media-upload-section" className="font-bold text-[15px] md:text-[17px] leading-6 pb-[25px] md:pb-10 pt-[25px] md:pt-[36px]">Upload media</p>
              <div className="flex flex-col md:flex-row gap-6 md:gap-10 mb-2">
                <ImageUpload ref={imageUploadRef} initialImage={formData.featured_image_url} onImageChange={handleImageChange} onUploadingChange={setIsImageUploading} />
                <div className="hidden md:block w-[1px] bg-[#DEDEDE]"></div>
                <MultipleImageUpload ref={galleryUploadRef} initialImages={formData.gallery} onImagesChange={handleGalleryChange} onUploadingChange={setIsGalleryUploading} />
              </div>
              {errors.images && (
                <p className="mt-1 mb-10 text-sm text-red-500">{errors.images[0]}</p>
              )}

              <hr />
              {(adOptions.temperament || adOptions.compatibility) && (
                <>
                  <p className="font-bold text-[15px] md:text-[17px] leading-6 pb-[25px] md:pb-[33px] pt-[25px] md:pt-[36px]">Cat Characteristics:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-2 mb-10">
                    {adOptions.temperament && (
                      <div>
                        <p className="uppercase text-base mb-6">Temperament</p>
                        <ul className="space-y-5 mb-10">
                          {adOptions.temperament?.options && Object.entries(adOptions.temperament.options).map(([key, value], index) => (
                            <li key={index}>
                              <div className="flex items-center mr-4">
                                <input name="temperament[]" onChange={handleChange} id={`temperament-checkbox-${index}`} type="checkbox" value={key} checked={formData.temperament?.includes(key)} className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                                <label htmlFor={`temperament-checkbox-${index}`} className="ml-2 text-base font-medium text-gray-900 dark:text-gray-300">{value}</label>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {adOptions.compatibility && (
                      <div>
                        <p className="uppercase text-base mb-6">Compatibility</p>
                        <ul className="space-y-5 mb-10">
                          {adOptions.compatibility?.options && Object.entries(adOptions.compatibility.options).map(([key, value], index) => (
                            <li key={index}>
                              <div className="flex items-center mr-4">
                                <input name="compatibility[]" onChange={handleChange} id={`compatibility-checkbox-${index}`} type="checkbox" value={key} checked={formData.compatibility?.includes(key)} className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                                <label htmlFor={`compatibility-checkbox-${index}`} className="ml-2 text-base font-medium text-gray-900 dark:text-gray-300">{value}</label>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}

              <hr />
              <p className="font-bold text-[17px] leading-6 pb-[23px] pt-[36px]">Additional information:</p>
              <textarea name="description" value={formData.description || ''} onChange={handleChange} id="description" rows="8" className="mb-9 block p-2.5 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"></textarea>

              <div className="flex justify-between">
                <Link to="/my/ads" className="focus:outline-none min-w-[113px] text-[#832EE6] font-bold bg-white hover:bg-gray-50 border-2 border-[#832EE6] rounded-[19px] text-sm px-5 py-2.5 mb-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700 max-h-[37px] flex items-center justify-center">CANCEL</Link>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="focus:outline-none min-w-[113px] text-white font-bold bg-[#832EE6] hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 rounded-[19px] text-sm px-5 py-2.5 mb-2 dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-900 max-h-[37px] flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>SUBMITTING...</span>
                    </>
                  ) : 'SUBMIT'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  )
}

export default EditAd
